import React from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { BankTransfer } from 'types/med'
import Stack from '../layout/Stack'
import { StackItemTextField } from '../shorthands/StackItemTextField'
import placeholder from 'config/placeholder'
import { Button, MenuItem, Paper, Select } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { StackItem } from '../layout/StackItem'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import ModalScrollContent from '../layout/ModalScrollContent'
import { emptyBankTransferSettings } from 'app/medical/utils'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    padding: theme.spacing(1, 0),
  },
  textWrapper: {
    flexWrap: 'wrap',
  },
  text: {
    display: 'inline-flex',
    paddingRight: theme.spacing(2),
  },
  label: {
    wordBreak: 'keep-all',
  },
  paymentDeadlineLabel: {
    marginRight: theme.spacing(1),
  },
  select: {
    width: '115px',
  },
}))

const accountTypes: BankTransfer['account_type'][] = ['普通', '当座', '定期']
// NOTE: Selectでnullが指定できないため、nullは内部的に '' として扱う
const paymentDeadlines: BankTransfer['payment_deadline'][] = [
  '',
  '7',
  '14',
  '21',
  '28',
]

type BillingBankModalProps = {
  /** 口座設定 */
  bankTransfer?: BankTransfer

  /** 保存ボタンが押された時に呼ばれる */
  onSubmit: (data: BankTransfer) => void

  /** キャンセルされた時に呼ばれる */
  onClose: () => void
}
const BillingBankModal: React.FC<BillingBankModalProps> = ({
  bankTransfer = emptyBankTransferSettings,
  onSubmit,
  onClose,
}: BillingBankModalProps) => {
  const { t } = useTranslation()

  const { classes } = useStyles()

  const { payment_deadline } = bankTransfer

  const schema = yup.object().shape({
    bank_name: yup.string().required().max(15),
    bank_number: yup
      .string()
      .matches(/^\d{4}$/, t('{{n}}桁の数字を入力してください', { n: 4 })),
    branch_name: yup.string().required().max(15),
    branch_number: yup
      .string()
      .matches(/^\d{3}$/, t('{{n}}桁の数字を入力してください', { n: 3 })),
    account_type: yup.string().required().length(2).oneOf(accountTypes),
    account_number: yup.string().matches(
      /^\d{4,7}$/,
      t('{{min}}桁〜{{max}}桁の数字を入力してください', {
        min: 4,
        max: 7,
      })
    ),
    account_name: yup.string().required().max(30),
    payment_deadline: yup
      .string()
      .transform((v) => v || '')
      .oneOf(paymentDeadlines),
  })

  const defaultValues: FieldValues = {
    ...bankTransfer,
    payment_deadline: payment_deadline === null ? '' : payment_deadline, // NOTE: Selectで扱うためにnullを''に変換する
  }

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  return (
    <Paper>
      <ModalScrollContent
        footer={
          <Stack align="right" borderTop>
            <MarginRightCancelButton onClick={onClose} />
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={!isValid || !isDirty}
              onClick={handleSubmit((data) => {
                onSubmit({
                  ...(data as BankTransfer),
                  payment_deadline: (data.payment_deadline ||
                    null) as BankTransfer['payment_deadline'], // NOTE: '' を nullに変換する
                })
              })}
            >
              {t('保存')}
            </Button>
          </Stack>
        }
      >
        <div className={classes.form}>
          <StackItemTextField
            label={<div className={classes.label}>{t('金融機関名')}</div>}
            name="bank_name"
            control={control}
            placeholder={placeholder().bank_name}
            errorMessage={errors?.bank_name?.message}
            fullWidth
          />
          <StackItemTextField
            label={<div className={classes.label}>{t('金融機関コード')}</div>}
            name="bank_number"
            control={control}
            placeholder={placeholder().bank_number}
            errorMessage={errors?.bank_number?.message}
            fullWidth
          />
          <StackItemTextField
            label={<div className={classes.label}>{t('支店名')}</div>}
            name="branch_name"
            control={control}
            placeholder={placeholder().branch_name}
            errorMessage={errors?.branch_name?.message}
            fullWidth
          />
          <StackItemTextField
            label={<div className={classes.label}>{t('支店番号')}</div>}
            name="branch_number"
            control={control}
            placeholder={placeholder().branch_number}
            errorMessage={errors?.branch_number?.message}
            fullWidth
          />
          <StackItem
            label={<div className={classes.label}>{t('預金種別')}</div>}
          >
            <Controller
              control={control}
              name="account_type"
              render={({ field }) => (
                <Select
                  {...field}
                  defaultValue={'普通'}
                  variant="outlined"
                  className={classes.select}
                >
                  {accountTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(type)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </StackItem>

          <StackItemTextField
            label={<div className={classes.label}>{t('口座番号')}</div>}
            name="account_number"
            control={control}
            placeholder={placeholder().account_number}
            fullWidth
            errorMessage={errors?.account_number?.message}
          />

          <StackItemTextField
            label={<div className={classes.label}>{t('口座名義')}</div>}
            name="account_name"
            control={control}
            placeholder={placeholder().account_name_kana}
            errorMessage={errors?.account_name?.message}
            fullWidth
          />

          <StackItem
            label={<div className={classes.label}>{t('振込期限目安')}</div>}
          >
            <span className={classes.paymentDeadlineLabel}>
              {t('請求日から')}
            </span>
            <Controller
              control={control}
              name="payment_deadline"
              render={({ field }) => (
                <Select
                  {...field}
                  variant="outlined"
                  displayEmpty
                  defaultValue="14"
                  className={classes.select}
                  onChange={(event) => {
                    field.onChange(event.target.value || '')
                  }}
                >
                  {paymentDeadlines.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day
                        ? `${t('{{dateCount}}日', { dateCount: day })}`
                        : t('なし')}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </StackItem>
        </div>
      </ModalScrollContent>
    </Paper>
  )
}

export default BillingBankModal
